/**
 * Phép tính thuần của các màn tri thức: không gọi HTTP, không dựng chuỗi hiển thị — trả mã để view
 * tự dịch. Luật chép đúng backend (`modules/tri-thuc/backend`) để giao diện chặn sớm cùng chỗ server
 * chặn, nhưng server vẫn là chốt cuối.
 */
import type { CheDoChunk, Folder, KnowledgeFile, TrangThaiTaiLieu } from '../types.ts';

/** Trần một tệp tải lên (backend `TRI_THUC_UPLOAD_MAX_MB`, native 50 MB). */
export const TOI_DA_MB = 50;

/** Đuôi tệp backend nhận (`tep/loai-tep.ts`); `.doc` bị từ chối kèm hướng dẫn riêng. */
export const DUOI_NHAN = ['.pdf', '.docx', '.xlsx', '.xls', '.pptx', '.ppt', '.txt', '.jpg', '.jpeg', '.png', '.gif', '.webp'];

/** Giới hạn của quy tắc cắt chunk thủ công (`kho.validate.ts`). */
export const CHUNK = { kichThuoc: [200, 4000], chongLan: [0, 1000] } as const;

export function duoiCua(tenTep: string): string {
  return /\.[^./\\]+$/.exec(tenTep)?.[0]?.toLowerCase() ?? '';
}

export type LoiTep = 'doc' | 'khongNhan' | 'quaLon' | 'rong';

/** Kiểm tệp trước khi gửi; `null` là được. */
export function kiemTep(tep: { name: string; size: number }): LoiTep | null {
  const duoi = duoiCua(tep.name);
  if (duoi === '.doc') return 'doc';
  if (!DUOI_NHAN.includes(duoi)) return 'khongNhan';
  if (tep.size === 0) return 'rong';
  if (tep.size > TOI_DA_MB * 1024 * 1024) return 'quaLon';
  return null;
}

export type LoiMaKho = 'trong' | 'dai' | 'sai';

/** Mã bộ tri thức: chữ hoa, số, `_`, `-`, tối đa 20 ký tự (native `CreateKnowledgeBaseCommandValidator`). */
export function kiemMaKho(ma: string): LoiMaKho | null {
  const v = ma.trim();
  if (!v) return 'trong';
  if (v.length > 20) return 'dai';
  if (!/^[A-Z0-9_-]+$/.test(v)) return 'sai';
  return null;
}

export type LoiChunk = 'kichThuocThieu' | 'kichThuocNgoai' | 'chongLanThieu' | 'chongLanNgoai' | 'chongLanQuaLon';

/** Lỗi theo ô của quy tắc chunk thủ công; chế độ tự động không có lỗi. */
export function kiemChunk(che: CheDoChunk, kichThuoc: number | null, chongLan: number | null):
  { kichThuoc?: LoiChunk; chongLan?: LoiChunk } {
  if (che === 'tu_dong') return {};
  const ra: { kichThuoc?: LoiChunk; chongLan?: LoiChunk } = {};
  if (kichThuoc === null) ra.kichThuoc = 'kichThuocThieu';
  else if (kichThuoc < CHUNK.kichThuoc[0] || kichThuoc > CHUNK.kichThuoc[1]) ra.kichThuoc = 'kichThuocNgoai';
  if (chongLan === null) ra.chongLan = 'chongLanThieu';
  else if (chongLan < CHUNK.chongLan[0] || chongLan > CHUNK.chongLan[1]) ra.chongLan = 'chongLanNgoai';
  else if (kichThuoc !== null && chongLan >= kichThuoc) ra.chongLan = 'chongLanQuaLon';
  return ra;
}

export type LoiHieuLuc = 'thieuTu' | 'denKhongSauTu';

/** "Từ" bắt buộc khi nạp tệp mới, "đến" tuỳ chọn và phải sau "từ" (native). Ngày dạng `YYYY-MM-DD`. */
export function kiemHieuLuc(tu: string | null, den: string | null, batBuocTu: boolean): LoiHieuLuc | null {
  if (!tu && batBuocTu) return 'thieuTu';
  if (tu && den && den <= tu) return 'denKhongSauTu';
  return null;
}

export type TinhTrangHieuLuc = 'khongDat' | 'trong' | 'chuaToi' | 'het';

/**
 * Tệp đang ở đâu so với khoảng hiệu lực, tính theo ngày lịch `homNay` (`YYYY-MM-DD`). So **chuỗi**,
 * không qua `Date`: ngày người dùng chọn không có múi giờ, đổi sang `Date` là lệch một ngày ở UTC+7.
 */
export function tinhHieuLuc(tu: string | null, den: string | null, homNay: string): TinhTrangHieuLuc {
  if (!tu && !den) return 'khongDat';
  if (tu && homNay < tu) return 'chuaToi';
  if (den && homNay > den) return 'het';
  return 'trong';
}

/** Ngày lịch hôm nay ở giờ máy người dùng, dạng `YYYY-MM-DD`. */
export function ngayHomNay(bay: Date = new Date()): string {
  const hai = (n: number) => String(n).padStart(2, '0');
  return `${bay.getFullYear()}-${hai(bay.getMonth() + 1)}-${hai(bay.getDate())}`;
}

/**
 * "Đồng bộ lại" (`POST /files/{id}/reindex`): tệp phải kèm tệp và thuộc một bộ; đang chờ hay đang nạp
 * thì chỉ cho khi đã kẹt (server trả `409 TRI_THUC_INDEX_IN_PROGRESS` ở các ca khác).
 */
export function dongBoLaiDuoc(f: Pick<KnowledgeFile, 'has_file' | 'knowledge_base_id' | 'index_status' | 'index_stuck'>): boolean {
  if (!f.has_file || !f.knowledge_base_id) return false;
  if (f.index_status === 'CHO_INDEX' || f.index_status === 'DANG_INDEX') return f.index_stuck;
  return true;
}

/** Tệp cần người dùng để mắt: nạp lỗi, hoặc kẹt trong hàng đợi. */
export function canDeMat(f: Pick<KnowledgeFile, 'index_status' | 'index_stuck'>): boolean {
  return f.index_status === 'LOI' || f.index_stuck;
}

export type ThaoTacTaiLieu = 'submit-review' | 'approve' | 'return-draft' | 'request-revision' | 'archive' | 'rollback';

/** Trạng thái sau mỗi thao tác — chép `tai-lieu/trang-thai.ts` của backend (bảng của native `KnowledgeDocument`). */
const CHUYEN: Record<ThaoTacTaiLieu, Partial<Record<TrangThaiTaiLieu, TrangThaiTaiLieu>>> = {
  'submit-review': { DRAFT: 'REVIEW' },
  approve: { REVIEW: 'APPROVED' },
  'return-draft': { REVIEW: 'DRAFT' },
  'request-revision': { REVIEW: 'DRAFT' },
  archive: { APPROVED: 'ARCHIVED' },
  rollback: { REVIEW: 'DRAFT', APPROVED: 'REVIEW', ARCHIVED: 'APPROVED' },
};

const THU_TU: ThaoTacTaiLieu[] = ['submit-review', 'approve', 'return-draft', 'request-revision', 'archive', 'rollback'];

/** Thao tác hợp lệ theo trạng thái; "phiên bản mới" thì luôn được nên không nằm ở đây. */
export function thaoTacDuoc(trangThai: TrangThaiTaiLieu): readonly ThaoTacTaiLieu[] {
  return THU_TU.filter((x) => CHUYEN[x][trangThai] !== undefined);
}

/** Trạng thái tài liệu sẽ sang nếu làm `thaoTac`; `null` khi không hợp lệ (server trả `409`). */
export function trangThaiSau(thaoTac: ThaoTacTaiLieu, tu: TrangThaiTaiLieu): TrangThaiTaiLieu | null {
  return CHUYEN[thaoTac][tu] ?? null;
}

export interface NutCay {
  thuMuc: Folder;
  con: NutCay[];
}

/**
 * Dựng cây thư mục từ danh sách phẳng, sắp theo tên. Thư mục mồ côi (cha không còn) đưa lên gốc; vòng
 * lặp (dữ liệu hỏng) bị cắt, không treo trình duyệt.
 */
export function dungCay(thuMuc: readonly Folder[], soSanh: (a: string, b: string) => number = (a, b) => a.localeCompare(b)):
  NutCay[] {
  const theoId = new Map(thuMuc.map((f) => [f.id, f]));
  const con = new Map<string | null, Folder[]>();
  for (const f of thuMuc) {
    const cha = f.parent_id && theoId.has(f.parent_id) ? f.parent_id : null;
    con.set(cha, [...(con.get(cha) ?? []), f]);
  }
  const daQua = new Set<string>();
  const dung = (cha: string | null): NutCay[] =>
    (con.get(cha) ?? [])
      .filter((f) => !daQua.has(f.id))
      .sort((a, b) => soSanh(a.name, b.name))
      .map((f) => {
        daQua.add(f.id);
        return { thuMuc: f, con: dung(f.id) };
      });
  return dung(null);
}

/** Chuỗi thư mục từ gốc tới thư mục `id` (kể cả nó) — cho thanh đường dẫn bấm được. */
export function chuoiThuMuc(thuMuc: readonly Folder[], id: string): Folder[] {
  const theoId = new Map(thuMuc.map((f) => [f.id, f]));
  const ra: Folder[] = [];
  const daQua = new Set<string>();
  for (let f = theoId.get(id); f && !daQua.has(f.id); f = f.parent_id ? theoId.get(f.parent_id) : undefined) {
    daQua.add(f.id);
    ra.unshift(f);
  }
  return ra;
}

/** Đường dẫn tên từ gốc tới thư mục (`Hợp đồng / 2026`), để hiện trong ô chọn thư mục. */
export function duongDanThuMuc(thuMuc: readonly Folder[], id: string): string[] {
  return chuoiThuMuc(thuMuc, id).map((f) => f.name);
}

/** Thư mục con trực tiếp của `id` (`null` là gốc bộ) trên cây đã dựng; thư mục không có thì rỗng. */
export function conCua(cay: readonly NutCay[], id: string | null): NutCay[] {
  return id === null ? [...cay] : timNut(cay, id)?.con ?? [];
}

function timNut(cay: readonly NutCay[], id: string): NutCay | undefined {
  for (const nut of cay) {
    const thay = nut.thuMuc.id === id ? nut : timNut(nut.con, id);
    if (thay) return thay;
  }
  return undefined;
}

/** Mọi thư mục trên cây theo thứ tự duyệt sâu, kèm độ sâu — cho ô chọn thư mục đích. */
export function duoiCay(cay: readonly NutCay[], sau = 0): Array<{ thuMuc: Folder; sau: number }> {
  return cay.flatMap((n) => [{ thuMuc: n.thuMuc, sau }, ...duoiCay(n.con, sau + 1)]);
}
